import React, { ReactNode, useMemo } from "react";
import { Box } from "@mui/joy";
import { Link } from "react-router-dom";
import CustomModal from "components/common/CustomModal";
import useMixpanelWeb from "../hooks/useMixpanelWeb";
import { sendMessage } from "./webview";
import { useRecoilValue } from "recoil";
import { userAtom } from "../recoil/userAtom";
import { decideExtendedRefund, decideInitialRefund } from "./refundPolicyUtils";
import { handleMailto } from "utils/mailtoUtils";
import { useChannelTalkContext } from "../contexts/ChannelTalkContext";

function SupportChannelTalkLink({ text }: { text: string }) {
  const { openChat } = useChannelTalkContext();
  return (
    <>
      <Box
        component={Link}
        to="#"
        onClick={(e) => {
          e.preventDefault();
          openChat();
        }}
        sx={{
          textDecoration: "underline",
          textUnderlineOffset: "2px",
          color: (theme) => theme.vars.palette.Content.Medium,
          cursor: "pointer",
          fontWeight: 500,
        }}
      >
        {text}
      </Box>
    </>
  );
}

function RefundLink({ text }: { text: string }) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const setMixPanel = useMixpanelWeb({
    eventName: "환불 신청 버튼 클릭",
  });

  return (
    <>
      <Box
        component={Link}
        to="#"
        onClick={(e) => {
          e.preventDefault();
          setMixPanel();
          handleMailto("support@orwellhealth.org", () => setIsModalOpen(true));
        }}
        sx={{
          textDecoration: "underline",
          textUnderlineOffset: "2px",
          color: (theme) => theme.vars.palette.Content.Highlight,
          cursor: "pointer",
          fontWeight: 500,
        }}
      >
        {text}
      </Box>
      <CustomModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="고객센터"
        subtitle={"support@orwellhealth.org\n영업일 기준 1일 내로 답변 드려요."}
        actions={[
          {
            label: "확인",
            onClick: () => setIsModalOpen(false),
            variant: "Solid",
          },
        ]}
      />
    </>
  );
}

// 신규: 최초 이용 환불 및 연장 이용 환불 링크 (클릭 시 팝업만 표시)
function InitialRefundLink({ text }: { text: string }) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const user = useRecoilValue(userAtom);

  const setMixPanel = useMixpanelWeb({
    eventName: "환불 신청 버튼 클릭",
  });

  const { title, subtitle } = useMemo(() => {
    const d = decideInitialRefund({
      user: user!,
      limitDays: user?.refundPolicyType === "c" ? 14 : 7,
    });
    return { title: d.title, subtitle: d.subtitle } as const;
  }, [user]);

  return (
    <>
      <Box
        component={Link}
        to="#"
        onClick={(e) => {
          e.preventDefault();
          setMixPanel();
          setIsModalOpen(true);
        }}
        sx={{
          textDecoration: "underline",
          textUnderlineOffset: "2px",
          color: (theme) => theme.vars.palette.Content.Highlight,
          cursor: "pointer",
          fontWeight: 500,
        }}
      >
        {text}
      </Box>
      <CustomModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
        subtitle={subtitle}
        actions={[
          {
            label: "확인",
            onClick: () => setIsModalOpen(false),
            variant: "Solid",
          },
        ]}
      />
    </>
  );
}

function ExtendedRefundLink({ text }: { text: string }) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const user = useRecoilValue(userAtom);
  const { openChat } = useChannelTalkContext();

  const { key, title, subtitle } = useMemo(() => {
    const d = decideExtendedRefund(user);
    return { key: d.key, title: d.title, subtitle: d.subtitle } as const;
  }, [user]);

  return (
    <>
      <Box
        component={Link}
        to="#"
        onClick={(e) => {
          e.preventDefault();
          if (key === "CHANNEL_TALK") {
            openChat();
          } else {
            setIsModalOpen(true);
          }
        }}
        sx={{
          textDecoration: "underline",
          textUnderlineOffset: "2px",
          color: (theme) => theme.vars.palette.Content.Highlight,
          cursor: "pointer",
          fontWeight: 500,
        }}
      >
        {text}
      </Box>
      <CustomModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={title}
        subtitle={subtitle}
        actions={[
          {
            label: "확인",
            onClick: () => setIsModalOpen(false),
            variant: "Solid",
          },
        ]}
      />
    </>
  );
}

/**
 * Converts special phrases and URLs in text to clickable links.
 * - "환불 신청하기 >" → RefundLink (mailto + 팝업)
 * - "최초 이용 환불 신청하기 >" → InitialRefundLink (팝업)
 * - "연장 이용 환불 신청하기 >" → ExtendedRefundLink (팝업)
 * - Converts URLs inside parentheses into clickable links (keeps parentheses).
 * @param text The text to process
 * @returns React elements with links injected
 */
export const linkifyText = (text: string): ReactNode => {
  if (!text) return null;

  const PHRASES = [
    { text: "고객센터", Comp: SupportChannelTalkLink },
    { text: "환불 신청하기 >", Comp: RefundLink },
    { text: "최초 이용 환불 신청하기 >", Comp: InitialRefundLink },
    { text: "연장 이용 환불 신청하기 >", Comp: ExtendedRefundLink },
  ] as const;

  // Helper: linkify URLs inside parentheses within a given plain text
  const linkifyUrlsInText = (plain: string): ReactNode => {
    if (!plain) return null;

    // Regex: (https://...)
    const urlInParenthesesRegex = /\((https?:\/\/[^\s)]+)\)/g;

    const matches: RegExpExecArray[] = [];
    let m: RegExpExecArray | null;
    while ((m = urlInParenthesesRegex.exec(plain)) !== null) {
      matches.push(m);
    }

    if (matches.length === 0) return plain;

    const fullMatches = matches.map((m) => m[0]);
    const urls = matches.map((m) => m[1]);

    const exactMatchesPattern = new RegExp(
      fullMatches
        .map((fm) => fm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .join("|"),
      "g"
    );

    const parts = plain.split(exactMatchesPattern);

    const nodes: ReactNode[] = [];
    parts.forEach((part, index) => {
      if (part) nodes.push(part);

      if (index < urls.length) {
        nodes.push("(");
        nodes.push(
          <Box
            key={`url-${index}`}
            component="a"
            href={urls[index]}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              // In in-app WebView, delegate to native to open external browser
              if ((window as any).ReactNativeWebView?.postMessage) {
                e.preventDefault();
                try {
                  sendMessage(
                    JSON.stringify({
                      event: "open_link",
                      params: { url: urls[index] },
                    })
                  );
                } catch (_) {
                  // Fallback: try window.open if bridge fails
                  window.open(urls[index], "_blank");
                }
              }
            }}
            sx={{
              textDecoration: "underline",
              color: (theme) => theme.vars.palette.Content.Medium,
              cursor: "pointer",
            }}
          >
            {urls[index]}
          </Box>
        );
        nodes.push(")");
      }
    });

    return nodes;
  };

  // Build a regex that matches any of the phrases
  const phrasePattern = new RegExp(
    PHRASES.map((p) => p.text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"),
    "g"
  );

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = phrasePattern.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);
    if (before) {
      const processed = linkifyUrlsInText(before);
      if (Array.isArray(processed)) nodes.push(...processed);
      else if (processed) nodes.push(processed);
    }

    const matchedText = match[0];
    const phrase = PHRASES.find((p) => p.text === matchedText)!;
    const Comp = phrase.Comp as (props: { text: string }) => JSX.Element;
    nodes.push(<Comp key={`phrase-${match.index}`} text={matchedText} />);

    lastIndex = match.index + matchedText.length;
  }

  const tail = text.slice(lastIndex);
  if (tail) {
    const processed = linkifyUrlsInText(tail);
    if (Array.isArray(processed)) nodes.push(...processed);
    else if (processed) nodes.push(processed);
  }

  // If no phrases matched, just linkify URLs
  if (nodes.length === 0) return linkifyUrlsInText(text);

  return nodes;
};
